/**
 * Chat usage data from the server: daily/monthly chat and transcript quota
 * (profile usage), audio upload limits and transcription minutes. The screen
 * decides when to (re)load; nothing here guesses plan limits (CHAT-21).
 */
import { useCallback, useState } from 'react';

import { transcriptionAPI, userProfileAPI } from '../../services/api';
import type { TranscriptionFormatsResponse, TranscriptionUsageResponse } from '../../types';
import type { ChatQuotaStatus } from './chatUsage';

export function useChatQuota() {
  const [quotaStatus, setQuotaStatus] = useState<ChatQuotaStatus | null>(null);
  const [quotaLoadFailed, setQuotaLoadFailed] = useState(false);
  const [audioUploadLimits, setAudioUploadLimits] = useState<TranscriptionFormatsResponse | null>(null);
  const [transcriptionUsage, setTranscriptionUsage] = useState<TranscriptionUsageResponse | null>(null);

  const loadQuotaStatus = useCallback(async () => {
    try {
      const profile = await userProfileAPI.getProfile();
      const usage = profile.usage;
      setQuotaStatus({
        chatLimitExceeded: usage.daily_chats_used >= usage.daily_chats_limit
          || usage.monthly_chats_used >= usage.monthly_chats_limit,
        transcriptLimitExceeded: usage.monthly_transcripts_used >= usage.monthly_transcripts_limit,
        usage: {
          daily_chats_used: usage.daily_chats_used,
          daily_chats_limit: usage.daily_chats_limit,
          monthly_chats_used: usage.monthly_chats_used,
          monthly_chats_limit: usage.monthly_chats_limit,
          monthly_transcripts_used: usage.monthly_transcripts_used,
          monthly_transcripts_limit: usage.monthly_transcripts_limit,
        },
      });
      setQuotaLoadFailed(false);
    } catch (error) {
      console.warn('Failed to load quota status:', error);
      setQuotaLoadFailed(true);
    }
  }, []);

  const loadAudioUploadLimits = useCallback(async () => {
    try {
      setAudioUploadLimits(await transcriptionAPI.getFormats());
    } catch (error) {
      console.warn('Failed to load audio upload limits:', error);
    }
  }, []);

  const loadTranscriptionUsage = useCallback(async () => {
    try {
      setTranscriptionUsage(await transcriptionAPI.getUsage());
    } catch (error) {
      console.warn('Failed to load transcription usage:', error);
    }
  }, []);

  return {
    quotaStatus,
    quotaLoadFailed,
    audioUploadLimits,
    transcriptionUsage,
    loadQuotaStatus,
    loadAudioUploadLimits,
    loadTranscriptionUsage,
  };
}
